import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { 
  Plus, 
  Edit, 
  Trash2, 
  X,
  Loader,
  Video,
  PlayCircle,
  Youtube
} from 'lucide-react'
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, limit, startAfter } from 'firebase/firestore'
import { db, COLLECTIONS, auth } from '../firebase/config'
import toast from 'react-hot-toast'

const PAGE_SIZE = 50

const HelpCenter = () => {
  const [videoGuides, setVideoGuides] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [lastVisible, setLastVisible] = useState(null)
  const [hasMore, setHasMore] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingGuide, setEditingGuide] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    title: '',
    youtubeUrl: '',
    description: '',
    category: 'General',
    isActive: true,
    order: 0
  })

  const categories = ['General', 'Booking', 'Gift Card', 'Account', 'Payment', 'Other']

  // Extract video ID from YouTube URL (supports normal, embed, shorts, and direct IDs)
  const extractVideoId = (url) => {
    if (!url) return ''

    // Trim whitespace
    const trimmed = url.trim()

    // If it looks like a bare 11-char ID, accept it directly
    if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) {
      return trimmed
    }

    // Handle common YouTube URL formats including shorts
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([^&\n?#]+)/,
    ]

    for (const pattern of patterns) {
      const match = trimmed.match(pattern)
      if (match) return match[1]
    }

    return ''
  }

  // Get embed URL from video ID
  const getEmbedUrl = (videoId) => {
    if (!videoId) return ''
    return `https://www.youtube.com/embed/${videoId}`
  }

  useEffect(() => {
    fetchVideoGuides()
  }, [])

  const fetchVideoGuides = async (cursor = null, append = false) => {
    try {
      if (append) setLoadingMore(true)
      else setLoading(true)
      const base = collection(db, COLLECTIONS.VIDEO_GUIDES)
      const q = cursor
        ? query(base, orderBy('order', 'asc'), limit(PAGE_SIZE), startAfter(cursor))
        : query(base, orderBy('order', 'asc'), limit(PAGE_SIZE))
      const snapshot = await getDocs(q)
      const guides = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null
      setLastVisible(lastDoc)
      setHasMore(snapshot.docs.length === PAGE_SIZE)
      if (append) setVideoGuides(prev => [...prev, ...guides])
      else setVideoGuides(guides)
    } catch (error) {
      console.error('Error fetching video guides:', error)
      toast.error('Failed to fetch video guides')
      setHasMore(false)
    } finally {
      if (append) setLoadingMore(false)
      else setLoading(false)
    }
  }

  const loadMoreGuides = () => {
    if (!lastVisible || loadingMore) return
    fetchVideoGuides(lastVisible, true)
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) || 0 : value)
    }))
  }

  const handleAddNew = () => {
    setFormData({
      title: '',
      youtubeUrl: '',
      description: '',
      category: 'General',
      isActive: true,
      order: videoGuides.length
    })
    setEditingGuide(null)
    setShowAddModal(true)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleEdit = (guide) => {
    setFormData({
      title: guide.title || '',
      youtubeUrl: guide.youtubeUrl || '',
      description: guide.description || '',
      category: guide.category || 'General',
      isActive: guide.isActive !== undefined ? guide.isActive : true,
      order: guide.order || 0
    })
    setEditingGuide(guide)
    setShowAddModal(true)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (isSubmitting) return // Prevent multiple submissions
    
    if (!formData.title.trim()) {
      toast.error('Please enter a guide title')
      return
    }

    if (!formData.youtubeUrl.trim()) {
      toast.error('Please enter a YouTube URL or video ID')
      return
    }

    const videoId = extractVideoId(formData.youtubeUrl)
    if (!videoId) {
      toast.error('Invalid YouTube URL. Please enter a valid YouTube link or video ID')
      return
    }

    setIsSubmitting(true)
    try {
      const guideData = {
        title: formData.title.trim(),
        youtubeUrl: formData.youtubeUrl.trim(),
        youtubeVideoId: videoId,
        embedUrl: getEmbedUrl(videoId),
        description: formData.description.trim(),
        category: formData.category,
        isActive: formData.isActive,
        order: formData.order,
        createdAt: editingGuide ? editingGuide.createdAt : new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: auth.currentUser?.uid || 'admin'
      }

      if (editingGuide) {
        await updateDoc(doc(db, COLLECTIONS.VIDEO_GUIDES, editingGuide.id), guideData)
        toast.success('Video guide updated successfully')
      } else {
        await addDoc(collection(db, COLLECTIONS.VIDEO_GUIDES), guideData)
        toast.success('Video guide added successfully')
      }

      setShowAddModal(false)
      setEditingGuide(null)
      fetchVideoGuides()
    } catch (error) {
      console.error('Error saving video guide:', error)
      toast.error('Failed to save video guide')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (guideId) => {
    if (!window.confirm('Are you sure you want to delete this video guide?')) {
      return
    }

    try {
      await deleteDoc(doc(db, COLLECTIONS.VIDEO_GUIDES, guideId))
      toast.success('Video guide deleted successfully')
      fetchVideoGuides()
    } catch (error) {
      console.error('Error deleting video guide:', error)
      toast.error('Failed to delete video guide')
    }
  }

  const handleCloseModal = () => {
    setShowAddModal(false)
    setEditingGuide(null)
    setIsSubmitting(false)
    setFormData({
      title: '',
      youtubeUrl: '',
      description: '',
      category: 'General',
      isActive: true,
      order: 0
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Help Center</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Manage video guides for users</p>
        </div>
        <button
          onClick={handleAddNew}
          className="flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 py-2 rounded-lg shadow-lg transition-all duration-300 transform hover:scale-105"
        >
          <Plus className="w-5 h-5" />
          <span>Add New Guide</span>
        </button>
      </div>

      {/* Video Guides List */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Order</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Preview</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {videoGuides.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                    <Video className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No video guides yet. Click "Add New Guide" to get started.</p>
                  </td>
                </tr>
              ) : (
                videoGuides.map((guide) => (
                  <tr key={guide.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {guide.order || 0}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{guide.title}</div>
                      {guide.description && (
                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-1">
                          {guide.description}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                        {guide.category || 'General'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        guide.isActive 
                          ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' 
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                      }`}>
                        {guide.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {guide.youtubeVideoId && (
                        <a
                          href={`https://www.youtube.com/watch?v=${guide.youtubeVideoId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-1 text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          <Youtube className="w-4 h-4" />
                          <span className="text-xs">View on YouTube</span>
                        </a>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(guide)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(guide.id)}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {!loading && videoGuides.length > 0 && (
          <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-3 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50">
            <p className="text-xs text-gray-500 dark:text-gray-400">Load more only when needed to reduce reads.</p>
            <button
              type="button"
              onClick={loadMoreGuides}
              disabled={!hasMore || loadingMore}
              className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg text-xs font-medium bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingMore ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Loading...</span>
                </>
              ) : hasMore ? (
                <span>Load more guides</span>
              ) : (
                <span>No more</span>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Modal - portaled so header is below top bar */}
      {showAddModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-start justify-center overflow-y-auto p-4 pt-20">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl my-auto max-h-[calc(100vh-5rem)] flex flex-col overflow-hidden border border-gray-200 dark:border-gray-700">
            <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 rounded-t-xl flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
                {editingGuide ? 'Edit Video Guide' : 'Add New Video Guide'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto flex-1 min-h-0">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Guide Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., How to Book a Service"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  YouTube URL or Video ID *
                </label>
                <input
                  type="text"
                  name="youtubeUrl"
                  value={formData.youtubeUrl}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="https://www.youtube.com/watch?v=VIDEO_ID or just VIDEO_ID"
                  required
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Paste the full YouTube URL or just the video ID
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Brief description of what this guide covers..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Category
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Display Order
                  </label>
                  <input
                    type="number"
                    name="order"
                    value={formData.order}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Active (visible to users)
                </label>
              </div>

              {formData.youtubeUrl && extractVideoId(formData.youtubeUrl) && (
                <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Preview:</p>
                  <div className="aspect-video w-full">
                    <iframe
                      className="w-full h-full rounded-lg"
                      src={getEmbedUrl(extractVideoId(formData.youtubeUrl))}
                      title="Video preview"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </div>
              )}

              <div className="flex items-center justify-end space-x-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg shadow-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      <span>{editingGuide ? 'Updating...' : 'Adding...'}</span>
                    </>
                  ) : (
                    <span>{editingGuide ? 'Update Guide' : 'Add Guide'}</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default HelpCenter

